import {
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
  Download,
  KeyRound as KeyIcon,
  Power,
  PowerOff,
  Radio,
  Wifi,
  WifiOff,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";


import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DEFAULT_PORTS,
  PROTOCOL_LABELS,
  SIN_CLASIFICAR,
  type Credential,
  type CredentialFacets,
  type CredentialFilters,
  type CredentialProtocol,
  type FacetKey,
  type TestResult,
  type ZabbixHostOption,
  type AltaEquipoResult,
  useAltaEquipo,
  useAsignarEnLote,
  useProbarEnLote,
  type ProbarEnLoteResult,
  useCreateCredential,
  useCredentialFacets,
  useGruposZabbix,
  useImportarZabbix,
  usePlantillasZabbix,
  useVistaPreviaImport,
  type OrigenZabbix,
  type ResultadoImportacion,
  type AsignarEnLoteResult,
  useCredentials,
  useDeleteCredential,
  useDeshabilitarCredencial,
  useHabilitarCredencial,
  useTestCredential,
  useTestCredentialAnon,
  useUpdateCredential,
  useZabbixHostSearch,
} from "@/hooks/useCredentials";

export function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // Se bloquea el desplazamiento del documento mientras el modal está abierto.
  // Sin esto, la rueda del ratón mueve la página de atrás en vez del modal, que
  // es justo lo que se siente como "no puedo llegar al botón".
  useEffect(() => {
    const previo = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previo;
    };
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      {/* Alto máximo y columna: el encabezado queda fijo y SOLO el contenido
          se desplaza. Antes la tarjeta crecía sin límite, así que con un
          formulario alto —el de llave privada, por ejemplo— el botón de
          guardar quedaba fuera de la pantalla y no había manera de alcanzarlo. */}
      <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-md flex-col rounded-lg border bg-card shadow-lg">
        <div className="flex shrink-0 items-center justify-between border-b px-5 py-4">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="rounded-md p-1 hover:bg-accent"
          >
            <X className="size-4" />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

// ── Selector de host Zabbix ────────────────────────────────────────────────────

function ZabbixHostPicker({
  value,
  onChange,
}: {
  value: ZabbixHostOption | null;
  onChange: (host: ZabbixHostOption | null) => void;
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { data, isFetching } = useZabbixHostSearch(q);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  function select(host: ZabbixHostOption) {
    onChange(host);
    setQ("");
    setOpen(false);
  }

  function clear() {
    onChange(null);
    setQ("");
  }

  if (value) {
    return (
      <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
        <CheckCircle2 className="size-4 shrink-0 text-green-500" />
        <span className="flex-1 truncate font-mono">{value.host}</span>
        {value.name !== value.host && (
          <span className="text-xs text-muted-foreground truncate">{value.name}</span>
        )}
        <button
          type="button"
          onClick={clear}
          className="ml-auto shrink-0 text-muted-foreground hover:text-foreground"
          aria-label="Quitar"
        >
          <X className="size-3" />
        </button>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => q.length >= 2 && setOpen(true)}
          placeholder="Buscar host en Zabbix..."
          className="pl-9"
        />
        {isFetching && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 size-4 animate-spin text-muted-foreground" />
        )}
      </div>
      {open && data && data.items.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full rounded-md border bg-background text-foreground shadow-lg max-h-48 overflow-y-auto text-sm">
          {data.items.map((h) => (
            <li key={h.hostid}>
              <button
                type="button"
                className="w-full px-3 py-2 text-left hover:bg-accent hover:text-accent-foreground flex items-center gap-2"
                onClick={() => select(h)}
              >
                <span className="font-mono">{h.host}</span>
                {h.name !== h.host && (
                  <span className="text-xs text-muted-foreground">{h.name}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && q.length >= 2 && !isFetching && data?.items.length === 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-background text-muted-foreground px-3 py-2 text-sm shadow-lg">
          Sin resultados en Zabbix para "{q}"
        </div>
      )}
    </div>
  );
}

// ── Resultado de la prueba ─────────────────────────────────────────────────────

// Tres estados, no dos. Un "no concluyente" en ámbar y no en rojo es lo que
// evita que se borre una credencial buena que solo estaba inalcanzable: el
// 16-ago dos credenciales válidas de Windows quedaron marcadas como fallidas
// porque el probador únicamente hablaba SSH.
const ESTILO_ESTADO: Record<string, { clase: string; titulo: string }> = {
  ok: {
    clase: "bg-green-500/10 text-green-700 dark:text-green-400",
    titulo: "Credencial correcta",
  },
  rechazada: {
    clase: "bg-red-500/10 text-red-700 dark:text-red-400",
    titulo: "El equipo rechazó la credencial",
  },
  no_concluyente: {
    clase: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
    titulo: "No concluyente — no dice nada sobre la credencial",
  },
};

function TestResultBadge({ result }: { result: TestResult }) {
  const estilo = ESTILO_ESTADO[result.status] ?? ESTILO_ESTADO.no_concluyente;
  return (
    <div className={`rounded p-2 text-xs ${estilo.clase}`}>
      <span className="font-medium">
        {result.status === "ok" ? (
          <CheckCircle2 className="size-3 inline mr-1" />
        ) : (
          <WifiOff className="size-3 inline mr-1" />
        )}
        {estilo.titulo}
      </span>
      <div className="mt-0.5 opacity-90">
        {result.detail}
        {result.hostname ? ` — ${result.hostname}` : ""}
      </div>
    </div>
  );
}

// ── Formulario crear/editar ────────────────────────────────────────────────────

function CredentialForm({
  initial,
  onClose,
}: {
  initial?: Credential;
  onClose: () => void;
}) {
  const isEdit = !!initial;
  const create = useCreateCredential();
  const update = useUpdateCredential(initial?.id ?? "");
  const testAnon = useTestCredentialAnon();
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [username, setUsername] = useState(initial?.username ?? "");
  const [secret, setSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [secretType, setSecretType] = useState<"password" | "private_key">(
    initial?.secret_type ?? "password",
  );
  const [protocol, setProtocol] = useState<CredentialProtocol>(initial?.protocol ?? "ssh");
  const [port, setPort] = useState<string>(initial?.port ? String(initial.port) : "");
  const [client, setClient] = useState(initial?.client ?? "");
  const [deviceType, setDeviceType] = useState(initial?.device_type ?? "");
  const [site, setSite] = useState(initial?.site ?? "");
  const [testAddress, setTestAddress] = useState(initial?.test_address ?? "");

  // Se piden sin filtro: las sugerencias deben ofrecer todo el vocabulario
  // que ya existe, no solo lo que sobrevivió al filtro actual.
  const facetsAll = useCredentialFacets({});
  function sugerencias(k: "client" | "device_type" | "site"): string[] {
    return (facetsAll.data?.[k] ?? [])
      .map((v) => v.value)
      .filter((v) => v !== SIN_CLASIFICAR);
  }
  const [zabbixHost, setZabbixHost] = useState<ZabbixHostOption | null>(
    initial?.zabbix_host_id
      ? { hostid: initial.zabbix_host_id, host: initial.zabbix_host_name ?? "", name: initial.zabbix_host_name ?? "", ip: null }
      : null,
  );
  // Se precarga con la dirección de prueba: si el equipo solo se alcanza por
  // un reenviador, escribir su dirección real da "no concluyente" y parece
  // culpa de la credencial.
  const [testIp, setTestIp] = useState(initial?.test_address ?? "");
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [error, setError] = useState("");
  const isPending = create.isPending || update.isPending;

  // Cuando el usuario selecciona un host Zabbix, pre-llenar la IP de test
  function handleZabbixHostChange(host: ZabbixHostOption | null) {
    setZabbixHost(host);
    if (host?.ip) setTestIp(host.ip);
    setTestResult(null);
  }

  const portNumber = port.trim() ? Number(port) : null;

  async function handleTest() {
    if (!testIp || !username || !secret) return;
    setTestResult(null);
    try {
      const r = await testAnon.mutateAsync({
        name: name || "credencial",
        username,
        secret,
        ip: testIp,
        protocol,
        secret_type: secretType,
        port: portNumber,
      });
      setTestResult(r);
    } catch {
      setTestResult({
        ok: false,
        status: "no_concluyente",
        detail: "No se pudo hablar con AROC para lanzar la prueba.",
        hostname: null,
        error: "Error de red",
        protocol: null,
      });
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    try {
      if (isEdit) {
        await update.mutateAsync({
          ...(name !== initial?.name && { name }),
          ...(description !== initial?.description && { description }),
          ...(username !== initial?.username && { username }),
          ...(secret && { secret }),
          ...(secretType !== initial?.secret_type && { secret_type: secretType }),
          ...(protocol !== initial?.protocol && { protocol }),
          ...(portNumber !== (initial?.port ?? null) && { port: portNumber }),
          ...(client !== (initial?.client ?? "") && { client }),
          ...(deviceType !== (initial?.device_type ?? "") && { device_type: deviceType }),
          ...(site !== (initial?.site ?? "") && { site }),
          ...(testAddress !== (initial?.test_address ?? "") && { test_address: testAddress }),
          zabbix_host_id: zabbixHost?.hostid ?? null,
          zabbix_host_name: zabbixHost?.host ?? null,
        });
      } else {
        await create.mutateAsync({
          name, description, username, secret,
          secret_type: secretType,
          protocol,
          port: portNumber,
          client, device_type: deviceType, site,
          test_address: testAddress || null,
          zabbix_host_id: zabbixHost?.hostid ?? null,
          zabbix_host_name: zabbixHost?.host ?? null,
        });
      }
      onClose();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? ((err.body as { error?: { message?: string } })?.error?.message ?? err.message)
          : "Error al guardar";
      setError(msg);
    }
  }

  const canTest = !!username && !!secret && !!testIp;

  return (
    <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
      <div className="space-y-1">
        <Label>Nombre identificador</Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="ej: tilatina-asterisk-ssh, asterisk_root"
          required
          autoComplete="off"
        />
        <p className="text-xs text-muted-foreground">
          Valor que escribes en <code className="font-mono">credentials_ref</code> del perfil,
          y con el que el SOAR pide esta credencial. Se puede corregir.
        </p>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="space-y-1 col-span-2">
          <Label>Cómo se conecta</Label>
          <select
            value={protocol}
            onChange={(e) => {
              setProtocol(e.target.value as CredentialProtocol);
              setTestResult(null);
            }}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
          >
            {(Object.keys(PROTOCOL_LABELS) as CredentialProtocol[]).map((p) => (
              <option key={p} value={p}>{PROTOCOL_LABELS[p]}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label>Puerto</Label>
          <Input
            value={port}
            onChange={(e) => { setPort(e.target.value); setTestResult(null); }}
            placeholder={String(DEFAULT_PORTS[protocol])}
            inputMode="numeric"
            className="font-mono text-sm"
            autoComplete="off"
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label>Descripción <span className="text-muted-foreground">(opcional)</span></Label>
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="ej: SSH root del Asterisk Elastix"
          autoComplete="off"
        />
      </div>
      {/* Clasificación. Texto libre con sugerencias de lo que ya existe: una
          lista cerrada obligaría a tocar código cada vez que entre un cliente
          o un tipo de equipo nuevo. */}
      <div className="grid grid-cols-3 gap-2">
        <div className="space-y-1">
          <Label>Cliente</Label>
          <Input
            list="sug-cliente"
            value={client}
            onChange={(e) => setClient(e.target.value)}
            placeholder="ej: Holanda"
            autoComplete="off"
          />
          <datalist id="sug-cliente">
            {sugerencias("client").map((v) => <option key={v} value={v} />)}
          </datalist>
        </div>
        <div className="space-y-1">
          <Label>Tipo de equipo</Label>
          <Input
            list="sug-tipo"
            value={deviceType}
            onChange={(e) => setDeviceType(e.target.value)}
            placeholder="ej: router"
            autoComplete="off"
          />
          <datalist id="sug-tipo">
            {sugerencias("device_type").map((v) => <option key={v} value={v} />)}
          </datalist>
        </div>
        <div className="space-y-1">
          <Label>Sitio</Label>
          <Input
            list="sug-sitio"
            value={site}
            onChange={(e) => setSite(e.target.value)}
            placeholder="ej: Mochis"
            autoComplete="off"
          />
          <datalist id="sug-sitio">
            {sugerencias("site").map((v) => <option key={v} value={v} />)}
          </datalist>
        </div>
      </div>
      <div className="space-y-1">
        <Label>Dirección de prueba (opcional)</Label>
        <Input
          value={testAddress}
          onChange={(e) => setTestAddress(e.target.value)}
          placeholder="ej: 209.216.230.203"
          autoComplete="off"
        />
        <p className="text-xs text-muted-foreground">
          Por dónde entra AROC, cuando no coincide con dónde está el equipo.
          Déjalo vacío y se usa la dirección del host en Zabbix. El puerto va
          arriba.
        </p>
      </div>
      <div className="space-y-1">
        <Label>Usuario</Label>
        <Input
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="ej: root, admin"
          required
          autoComplete="off"
        />
      </div>
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Label>
            {secretType === "private_key"
              ? isEdit ? "Nueva llave privada (vacío = no cambiar)" : "Llave privada"
              : isEdit ? "Nuevo password (vacío = no cambiar)" : "Password"}
          </Label>
          <select
            value={secretType}
            onChange={(e) => {
              setSecretType(e.target.value as "password" | "private_key");
              setSecret("");
              setTestResult(null);
            }}
            className="h-7 rounded-md border border-input bg-transparent px-2 text-xs"
          >
            <option value="password">Password</option>
            <option value="private_key">Llave privada</option>
          </select>
        </div>
        {secretType === "private_key" ? (
          <>
            <textarea
              value={secret}
              onChange={(e) => { setSecret(e.target.value); setTestResult(null); }}
              placeholder={isEdit ? "••••••••" : "-----BEGIN OPENSSH PRIVATE KEY-----"}
              required={!isEdit}
              rows={4}
              className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 font-mono text-xs shadow-sm"
              autoComplete="off"
              spellCheck={false}
            />
            <input
              type="file"
              accept=".pem,.key,text/plain,application/x-pem-file"
              onChange={async (e) => {
                const archivo = e.target.files?.[0];
                if (!archivo) return;
                // Se lee en el navegador y se manda por el mismo campo: la
                // llave nunca viaja como archivo ni queda en un formulario
                // multipart intermedio.
                setSecret(await archivo.text());
                setTestResult(null);
              }}
              className="block w-full text-xs text-muted-foreground file:mr-2 file:rounded file:border-0 file:bg-muted file:px-2 file:py-1 file:text-xs"
            />
            <p className="text-xs text-muted-foreground">
              Se valida al guardar. Las llaves con frase de paso todavía no se soportan.
            </p>
          </>
        ) : (
          <>
            <div className="relative">
              <Input
                type={showSecret ? "text" : "password"}
                value={secret}
                onChange={(e) => { setSecret(e.target.value); setTestResult(null); }}
                placeholder={isEdit ? "••••••••" : "Password del equipo"}
                required={!isEdit}
                className="pr-10"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowSecret((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showSecret ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">Se guarda cifrado. Nunca se muestra.</p>
          </>
        )}
      </div>
      <div className="space-y-1">
        <Label>
          Host en Zabbix{" "}
          <span className="text-muted-foreground">(opcional, recomendado)</span>
        </Label>
        <ZabbixHostPicker value={zabbixHost} onChange={handleZabbixHostChange} />
      </div>

      {/* ── Test de conexión inline ── */}
      <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Probar conexión</p>
        <div className="flex gap-2">
          <Input
            value={testIp}
            onChange={(e) => { setTestIp(e.target.value); setTestResult(null); }}
            placeholder="IP del equipo"
            className="font-mono text-sm"
            autoComplete="off"
          />
          <Button
            type="button"
            variant="secondary"
            disabled={!canTest || testAnon.isPending}
            onClick={handleTest}
            className="shrink-0"
          >
            {testAnon.isPending ? <Loader2 className="size-4 animate-spin" /> : <Wifi className="size-4" />}
          </Button>
        </div>
        {!canTest && (
          <p className="text-xs text-muted-foreground">
            Completa usuario, secreto e IP para probar.
          </p>
        )}
        {testResult && <TestResultBadge result={testResult} />}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {/* Pegado al fondo del área que se desplaza: con el formulario de llave
          privada, que es el más alto, guardar no debería obligar a recorrer
          todo el panel de prueba primero. Los márgenes negativos compensan el
          padding del contenedor para que la barra llegue de borde a borde. */}
      <div className="sticky bottom-0 -mx-5 -mb-4 flex justify-end gap-2 border-t bg-card px-5 py-3">
        <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isPending}>
          {isEdit ? "Guardar cambios" : "Crear credencial"}
        </Button>
      </div>
    </form>
  );
}

// ── Panel de prueba de conexión ────────────────────────────────────────────────

function TestPanel({ cred, onClose }: { cred: Credential; onClose: () => void }) {
  const testMut = useTestCredential(cred.id);
  // La dirección de prueba gana desde el arranque: si el equipo sólo se alcanza
  // por un reenviador, su IP de Zabbix da "no concluyente" y parece culpa de la
  // credencial. Sin esto, un equipo sin host Zabbix (como los routers Holanda)
  // abría el panel con el campo vacío y no se podía probar.
  const [ip, setIp] = useState(cred.test_address ?? "");
  const [ipFromUser, setIpFromUser] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);

  // Sólo si NO hay dirección de prueba se cae al host de Zabbix.
  const searchQ = cred.test_address ? "" : (cred.zabbix_host_name ?? "");
  const { data: zabbixData, isFetching: searchingIp } = useZabbixHostSearch(searchQ);
  useEffect(() => {
    if (ipFromUser || cred.test_address) return;
    const match = zabbixData?.items.find((h) => h.hostid === cred.zabbix_host_id) ?? zabbixData?.items[0];
    if (match?.ip) setIp(match.ip);
  }, [zabbixData, cred.zabbix_host_id, cred.test_address, ipFromUser]);

  async function handleTest(e: React.FormEvent) {
    e.preventDefault();
    setResult(null);
    try {
      const r = await testMut.mutateAsync({ ip });
      setResult(r);
    } catch {
      setResult({
        ok: false,
        status: "no_concluyente",
        detail: "No se pudo hablar con AROC para lanzar la prueba.",
        hostname: null,
        error: "Error de red",
        protocol: null,
      });
    }
  }

  return (
    <form onSubmit={handleTest} className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Prueba si <span className="font-mono font-medium">{cred.name}</span>{" "}
        puede conectarse
        {cred.zabbix_host_name ? (
          <> en <span className="font-mono">{cred.zabbix_host_name}</span></>
        ) : null}
        .
      </p>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input
            value={ip}
            onChange={(e) => { setIp(e.target.value); setIpFromUser(true); setResult(null); }}
            placeholder="ej: 192.168.0.1"
            required
            className="font-mono"
          />
          {searchingIp && !ip && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 size-4 animate-spin text-muted-foreground" />
          )}
        </div>
        <Button type="submit" disabled={testMut.isPending || !ip}>
          {testMut.isPending ? <Loader2 className="size-4 animate-spin" /> : "Probar"}
        </Button>
      </div>
      {result && <TestResultBadge result={result} />}
      <div className="flex justify-end">
        <Button type="button" variant="outline" onClick={onClose}>Cerrar</Button>
      </div>
    </form>
  );
}

// ── Riel de facetas ────────────────────────────────────────────────────────────

const FACET_TITULOS: Record<FacetKey, string> = {
  client: "Cliente",
  device_type: "Tipo de equipo",
  protocol: "Tipo de acceso",
  test_status: "Última prueba",
  site: "Sitio",
};

const ESTADO_LABEL: Record<string, string> = {
  ok: "Correcta",
  rechazada: "Rechazada",
  no_concluyente: "No concluyente",
  sin_probar: "Sin probar",
};

const ESTADO_PUNTO: Record<string, string> = {
  ok: "bg-green-500",
  rechazada: "bg-red-500",
  no_concluyente: "bg-amber-500",
  sin_probar: "bg-muted-foreground/40",
};

// Orden fijo para el estado: lo que necesita atención primero. Las demás
// facetas van por cantidad, que es como las devuelve el servidor.
const ORDEN_ESTADO = ["rechazada", "no_concluyente", "sin_probar", "ok"];

function etiquetaFaceta(k: FacetKey, v: string): string {
  if (v === SIN_CLASIFICAR) return SIN_CLASIFICAR;
  if (k === "protocol") return PROTOCOL_LABELS[v as CredentialProtocol] ?? v;
  if (k === "test_status") return ESTADO_LABEL[v] ?? v;
  return v;
}

function FacetRail({
  facets,
  filtros,
  onToggle,
  onClear,
}: {
  facets?: CredentialFacets;
  filtros: CredentialFilters;
  onToggle: (k: FacetKey, v: string) => void;
  onClear: (k: FacetKey) => void;
}) {
  const [buscaSitio, setBuscaSitio] = useState("");

  if (!facets) {
    return <div className="text-xs text-muted-foreground">Cargando filtros…</div>;
  }

  return (
    <div className="space-y-5">
      {(Object.keys(FACET_TITULOS) as FacetKey[]).map((k) => {
        let valores = [...(facets[k] ?? [])];
        if (k === "test_status") {
          valores.sort((a, b) => ORDEN_ESTADO.indexOf(a.value) - ORDEN_ESTADO.indexOf(b.value));
        }
        const buscable = k === "site" && valores.length > 8;
        if (buscable && buscaSitio.trim()) {
          const t = buscaSitio.trim().toLowerCase();
          valores = valores.filter((v) => v.value.toLowerCase().includes(t));
        }
        if (!valores.length && !(filtros[k] ?? []).length) return null;
        const activos = filtros[k] ?? [];

        return (
          <div key={k} className="space-y-0.5">
            <div className="flex items-center justify-between px-1 pb-1">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {FACET_TITULOS[k]}
              </span>
              {activos.length > 0 && (
                <button
                  type="button"
                  onClick={() => onClear(k)}
                  className="text-[10px] text-primary hover:underline"
                >
                  quitar
                </button>
              )}
            </div>
            {buscable && (
              <Input
                value={buscaSitio}
                onChange={(e) => setBuscaSitio(e.target.value)}
                placeholder="Filtrar sitio…"
                className="h-7 text-xs mb-1"
                aria-label="Filtrar sitio"
              />
            )}
            <div className={buscable ? "max-h-52 overflow-y-auto" : undefined}>
              {valores.map((v) => {
                const activo = activos.includes(v.value);
                return (
                  <button
                    key={v.value}
                    type="button"
                    aria-pressed={activo}
                    onClick={() => onToggle(k, v.value)}
                    className={`flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-[13px] hover:bg-accent ${
                      activo ? "bg-accent font-semibold" : ""
                    }`}
                  >
                    {k === "test_status" && (
                      <span className={`size-1.5 rounded-full shrink-0 ${ESTADO_PUNTO[v.value] ?? ""}`} />
                    )}
                    <span className="flex-1 truncate">{etiquetaFaceta(k, v.value)}</span>
                    <span className="text-xs tabular-nums text-muted-foreground">{v.count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Asignar credencial a varios equipos ────────────────────────────────────────

// Se aplica sobre el filtro que está puesto en la pantalla. Nació de que el
// inventario importado trae 38 routers de Holanda que comparten credencial:
// llenarlos uno por uno no es razonable.
function AsignarEnLotePanel({
  filtros,
  cuantos,
  descripcionFiltro,
  onClose,
}: {
  filtros: CredentialFilters;
  cuantos: number;
  descripcionFiltro: string;
  onClose: () => void;
}) {
  const asignar = useAsignarEnLote(filtros);
  const [username, setUsername] = useState("");
  const [secret, setSecret] = useState("");
  const [secretType, setSecretType] = useState<"password" | "private_key">("password");
  const [verSecreto, setVerSecreto] = useState(false);
  const [sobrescribir, setSobrescribir] = useState(false);
  const [error, setError] = useState("");
  const [hecho, setHecho] = useState<AsignarEnLoteResult | null>(null);

  if (hecho) {
    return (
      <div className="space-y-4">
        <div className="rounded-md border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-700 dark:text-green-400">
          <p className="font-medium">
            Credencial puesta a {hecho.afectadas} equipo{hecho.afectadas === 1 ? "" : "s"}.
          </p>
          {hecho.omitidas_ya_tenian > 0 && (
            <p className="mt-1 text-xs opacity-90">
              {hecho.omitidas_ya_tenian} ya tenían credencial y no se tocaron.
            </p>
          )}
          <p className="mt-2 text-xs opacity-90">
            Quedaron como “sin probar”. Pruébalas para confirmar que la credencial
            es la correcta.
          </p>
        </div>
        <div className="flex justify-end">
          <Button onClick={onClose}>Listo</Button>
        </div>
      </div>
    );
  }

  return (
    <form
      className="space-y-4"
      autoComplete="off"
      onSubmit={async (e) => {
        e.preventDefault();
        setError("");
        try {
          setHecho(
            await asignar.mutateAsync({
              username,
              secret,
              secret_type: secretType,
              sobrescribir,
            }),
          );
        } catch (err) {
          setError(
            err instanceof ApiError
              ? ((err.body as { error?: { message?: string } })?.error?.message ?? err.message)
              : "No se pudo asignar",
          );
        }
      }}
    >
      <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
        Se aplicará a los <span className="font-semibold tabular-nums">{cuantos}</span> equipos
        del filtro actual: <span className="text-muted-foreground">{descripcionFiltro}</span>
      </div>

      <div className="space-y-1">
        <Label>Usuario</Label>
        <Input value={username} onChange={(e) => setUsername(e.target.value)} required autoComplete="off" />
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Label>{secretType === "private_key" ? "Llave privada" : "Password"}</Label>
          <select
            value={secretType}
            onChange={(e) => { setSecretType(e.target.value as "password" | "private_key"); setSecret(""); }}
            className="h-7 rounded-md border border-input bg-transparent px-2 text-xs"
          >
            <option value="password">Password</option>
            <option value="private_key">Llave privada</option>
          </select>
        </div>
        {secretType === "private_key" ? (
          <textarea
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            rows={4}
            required
            spellCheck={false}
            placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
            className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 font-mono text-xs shadow-sm"
          />
        ) : (
          <div className="relative">
            <Input
              type={verSecreto ? "text" : "password"}
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              required
              className="pr-10"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setVerSecreto((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {verSecreto ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
        )}
      </div>

      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={sobrescribir}
          onChange={(e) => setSobrescribir(e.target.checked)}
          className="mt-0.5"
        />
        <span>
          Reemplazar también las que ya tienen credencial
          <span className="block text-xs text-muted-foreground">
            Por omisión sólo se llenan las que están vacías. Marca esto sólo si
            quieres pisar credenciales ya probadas.
          </span>
        </span>
      </label>

      {sobrescribir && (
        <p className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
          Vas a reemplazar credenciales que ya funcionaban. No hay deshacer.
        </p>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="sticky bottom-0 -mx-5 -mb-4 flex justify-end gap-2 border-t bg-card px-5 py-3">
        <Button type="button" variant="outline" onClick={onClose} disabled={asignar.isPending}>
          Cancelar
        </Button>
        <Button type="submit" disabled={asignar.isPending || !username || !secret}>
          {asignar.isPending ? "Asignando…" : `Asignar a ${cuantos}`}
        </Button>
      </div>
    </form>
  );
}

// ── Importar inventario desde Zabbix ───────────────────────────────────────────

// Zabbix sabe qué equipos hay, no con qué contraseña se entra. Se trae el
// inventario y las credenciales se ponen encima. Siempre con vista previa: el
// operador ve cuántos entran y cómo quedarían clasificados ANTES de escribir.
function ImportarZabbixPanel({ onClose }: { onClose: () => void }) {
  const [origen, setOrigen] = useState<OrigenZabbix>("cliente");
  const previa = useVistaPreviaImport(origen);
  const importar = useImportarZabbix();
  const [hecho, setHecho] = useState<ResultadoImportacion | null>(null);
  const [error, setError] = useState("");

  const sinConfigurar = previa.error instanceof ApiError && previa.error.status === 503;

  if (hecho) {
    return (
      <div className="space-y-4">
        <div className="rounded-md border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-700 dark:text-green-400">
          <p className="font-medium">Se importaron {hecho.importados} equipos.</p>
          <ul className="mt-1 text-xs space-y-0.5 opacity-90">
            {hecho.omitidos_ya_existian > 0 && (
              <li>{hecho.omitidos_ya_existian} ya estaban y no se tocaron.</li>
            )}
            {hecho.omitidos_sin_ip > 0 && (
              <li>
                {hecho.omitidos_sin_ip} entraron sin interfaz en Zabbix: no se
                podrán probar hasta que la tengan.
              </li>
            )}
          </ul>
          <p className="mt-2 text-xs opacity-90">
            Llegan <span className="font-medium">sin credencial</span>. Fíltralos
            por “sin clasificar” o por “Sin probar” para irlos completando.
          </p>
        </div>
        <div className="flex justify-end">
          <Button onClick={onClose}>Listo</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label>De qué Zabbix</Label>
        <select
          value={origen}
          onChange={(e) => setOrigen(e.target.value as OrigenZabbix)}
          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
        >
          <option value="cliente">Del NOC del cliente — ahí está el inventario completo</option>
          <option value="propio">De esta instancia — sólo los que ya vigila la nube</option>
        </select>
        <p className="text-xs text-muted-foreground">
          Sólo se lee. No se crea ni se modifica nada del lado del monitoreo.
        </p>
      </div>

      {sinConfigurar ? (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
          Ese Zabbix no está configurado en esta instancia.
        </div>
      ) : previa.isPending ? (
        <p className="text-sm text-muted-foreground">Leyendo el inventario…</p>
      ) : previa.data ? (
        <>
          <div className="grid grid-cols-3 gap-2 text-center">
            {[
              ["En Zabbix", previa.data.total_en_zabbix],
              ["Se importarían", previa.data.nuevos],
              ["Ya estaban", previa.data.ya_existen],
            ].map(([etiqueta, valor]) => (
              <div key={String(etiqueta)} className="rounded-md border p-2">
                <div className="text-lg font-semibold tabular-nums">{valor}</div>
                <div className="text-[11px] text-muted-foreground">{etiqueta}</div>
              </div>
            ))}
          </div>

          {previa.data.sin_ip > 0 && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              {previa.data.sin_ip} no tienen interfaz en Zabbix. Entran igual al
              inventario, pero no se van a poder probar hasta que la tengan.
            </p>
          )}

          <div className="rounded-md border max-h-56 overflow-y-auto divide-y">
            {previa.data.items
              .filter((i) => !i.ya_existe)
              .slice(0, 80)
              .map((i) => (
                <div key={i.nombre} className="px-2 py-1.5 text-xs">
                  <div className="font-mono">{i.nombre}</div>
                  <div className="flex gap-1.5 flex-wrap pt-0.5 text-[11px] text-muted-foreground">
                    {i.client ? <span className="rounded border px-1">{i.client}</span> : null}
                    {i.site ? <span className="rounded border px-1">{i.site}</span> : null}
                    {i.device_type ? (
                      <span className="rounded border px-1">{i.device_type}</span>
                    ) : (
                      <span className="text-amber-600 dark:text-amber-400">sin clasificar</span>
                    )}
                    <span className="rounded border px-1">{i.protocolo}</span>
                  </div>
                </div>
              ))}
          </div>
          <p className="text-xs text-muted-foreground">
            El cliente y el tipo salen de los grupos de Zabbix. Cuando los grupos
            se contradicen se deja en blanco: es preferible un hueco que un dato
            equivocado.
          </p>
        </>
      ) : null}

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="sticky bottom-0 -mx-5 -mb-4 flex justify-end gap-2 border-t bg-card px-5 py-3">
        <Button variant="outline" onClick={onClose} disabled={importar.isPending}>
          Cancelar
        </Button>
        <Button
          disabled={!previa.data || previa.data.nuevos === 0 || importar.isPending}
          onClick={async () => {
            setError("");
            try {
              setHecho(await importar.mutateAsync(origen));
            } catch (err) {
              setError(
                err instanceof ApiError
                  ? ((err.body as { error?: { message?: string } })?.error?.message ?? err.message)
                  : "No se pudo importar",
              );
            }
          }}
        >
          {importar.isPending
            ? "Importando…"
            : `Importar ${previa.data?.nuevos ?? 0} equipos`}
        </Button>
      </div>
    </div>
  );
}

// ── Alta de equipo en el monitoreo propio ──────────────────────────────────────

// Crea el host en el Zabbix de ESTA instancia y, si se pidió, le amarra la
// credencial. No se crean tableros en Grafana: el host entra a un grupo y los
// tableros existentes ya filtran por grupo — menos piezas que mantener.
function AltaEquipoPanel({
  cred,
  onClose,
}: {
  cred: Credential | null;
  onClose: () => void;
}) {
  const grupos = useGruposZabbix();
  const [buscaPlantilla, setBuscaPlantilla] = useState("");
  const plantillas = usePlantillasZabbix(buscaPlantilla);
  const alta = useAltaEquipo();

  const [nombre, setNombre] = useState(cred?.name ?? "");
  const [ip, setIp] = useState("");
  const [grupoId, setGrupoId] = useState("");
  const [plantillaIds, setPlantillaIds] = useState<string[]>([]);
  const [usarAgente, setUsarAgente] = useState(false);
  const [puerto, setPuerto] = useState("");
  const [error, setError] = useState("");
  const [hecho, setHecho] = useState<AltaEquipoResult | null>(null);

  // 503 = la instancia no tiene configurado su Zabbix propio. Es distinto de
  // un fallo: hay que decir qué falta, no "error al cargar".
  const sinConfigurar =
    grupos.error instanceof ApiError && grupos.error.status === 503;

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    try {
      const r = await alta.mutateAsync({
        nombre,
        ip,
        grupo_id: grupoId,
        plantilla_ids: plantillaIds,
        puerto: puerto.trim() ? Number(puerto) : null,
        usar_agente: usarAgente,
        credential_id: cred?.id ?? null,
      });
      setHecho(r);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? ((err.body as { error?: { message?: string } })?.error?.message ?? err.message)
          : "No se pudo dar de alta el equipo",
      );
    }
  }

  if (sinConfigurar) {
    return (
      <div className="space-y-4">
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
          Esta instancia todavía no tiene configurado su propio Zabbix. Falta
          definir <code className="font-mono">ZABBIX_PROPIO_API_URL</code> y{" "}
          <code className="font-mono">ZABBIX_PROPIO_API_TOKEN</code>.
          <p className="mt-2">
            El alta de equipos escribe, y a propósito no usa la configuración del
            Zabbix del cliente para hacerlo.
          </p>
        </div>
        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </div>
      </div>
    );
  }

  if (hecho) {
    return (
      <div className="space-y-4">
        <div className="rounded-md border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-700 dark:text-green-400">
          <p className="font-medium">
            {hecho.creado ? "Equipo dado de alta" : "El equipo ya existía en Zabbix"}
          </p>
          <p className="mt-1">
            <span className="font-mono">{hecho.nombre}</span> · host{" "}
            <span className="font-mono">{hecho.zabbix_host_id}</span>
            {hecho.credencial_vinculada ? " · credencial vinculada" : ""}
          </p>
          <p className="mt-2 text-xs opacity-90">
            Aparecerá en los tableros de Grafana que filtran por ese grupo, en
            cuanto Zabbix recolecte el primer dato.
          </p>
        </div>
        <div className="flex justify-end">
          <Button onClick={onClose}>Listo</Button>
        </div>
      </div>
    );
  }

  const listo = !!nombre && !!ip && !!grupoId;

  return (
    <form onSubmit={guardar} className="space-y-4" autoComplete="off">
      <p className="text-sm text-muted-foreground">
        Se crea en el Zabbix <span className="font-medium text-foreground">de esta instancia</span>,
        no en el del cliente.
      </p>
      <div className="space-y-1">
        <Label>Nombre del host</Label>
        <Input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="ej: holanda-mochis-router"
          className="font-mono"
          required
        />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="space-y-1 col-span-2">
          <Label>Dirección IP</Label>
          <Input
            value={ip}
            onChange={(e) => setIp(e.target.value)}
            placeholder="172.10.14.1"
            className="font-mono"
            required
          />
        </div>
        <div className="space-y-1">
          <Label>Puerto</Label>
          <Input
            value={puerto}
            onChange={(e) => setPuerto(e.target.value)}
            placeholder={usarAgente ? "10050" : "161"}
            inputMode="numeric"
            className="font-mono"
          />
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={usarAgente}
          onChange={(e) => setUsarAgente(e.target.checked)}
        />
        Tiene agente de Zabbix instalado
        <span className="text-xs text-muted-foreground">(si no, se da de alta por SNMP)</span>
      </label>
      <div className="space-y-1">
        <Label>Grupo</Label>
        <select
          value={grupoId}
          onChange={(e) => setGrupoId(e.target.value)}
          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
          required
        >
          <option value="">
            {grupos.isPending ? "Cargando…" : "Elige un grupo"}
          </option>
          {(grupos.data?.items ?? []).map((g) => (
            <option key={g.id} value={g.id}>{g.nombre}</option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground">
          Es lo que decide en qué tablero de Grafana aparece.
        </p>
      </div>
      <div className="space-y-1">
        <Label>Plantillas <span className="text-muted-foreground">(opcional)</span></Label>
        <Input
          value={buscaPlantilla}
          onChange={(e) => setBuscaPlantilla(e.target.value)}
          placeholder="Buscar plantilla… ej: Linux, SNMP, Windows"
          className="mb-1"
        />
        <div className="max-h-36 overflow-y-auto rounded-md border divide-y">
          {(plantillas.data?.items ?? []).slice(0, 40).map((p) => (
            <label key={p.id} className="flex items-center gap-2 px-2 py-1 text-sm hover:bg-accent">
              <input
                type="checkbox"
                checked={plantillaIds.includes(p.id)}
                onChange={(e) =>
                  setPlantillaIds((s) =>
                    e.target.checked ? [...s, p.id] : s.filter((x) => x !== p.id),
                  )
                }
              />
              <span className="font-mono text-xs">{p.nombre}</span>
            </label>
          ))}
          {!plantillas.isPending && (plantillas.data?.items ?? []).length === 0 && (
            <p className="px-2 py-2 text-xs text-muted-foreground">
              Sin plantillas para esa búsqueda.
            </p>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          El catálogo propio es corto y curado (Linux, SNMP de red, Windows).
          Sin plantilla el host queda creado pero no recolecta nada.
        </p>
      </div>
      {cred && (
        <p className="text-xs text-muted-foreground">
          Se vinculará la credencial <span className="font-mono">{cred.name}</span> a este host.
        </p>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="sticky bottom-0 -mx-5 -mb-4 flex justify-end gap-2 border-t bg-card px-5 py-3">
        <Button type="button" variant="outline" onClick={onClose} disabled={alta.isPending}>
          Cancelar
        </Button>
        <Button type="submit" disabled={!listo || alta.isPending}>
          {alta.isPending ? "Dando de alta…" : "Dar de alta"}
        </Button>
      </div>
    </form>
  );
}

// ── Página principal ───────────────────────────────────────────────────────────

const POR_PAGINA = 25;

export default function AdminCredentialsPage() {
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState<Record<FacetKey, string[]>>({
    client: [], device_type: [], protocol: [], test_status: [], site: [],
  });
  const [mostrarTodo, setMostrarTodo] = useState(false);
  const [pagina, setPagina] = useState(1);

  const filtros: CredentialFilters = { q: search, ...sel };
  const hayFiltro =
    search.trim() !== "" || (Object.keys(sel) as FacetKey[]).some((k) => sel[k].length > 0);
  const debeMostrar = hayFiltro || mostrarTodo;

  const facetsQuery = useCredentialFacets(filtros);
  const { data, isLoading } = useCredentials(
    filtros, POR_PAGINA, (pagina - 1) * POR_PAGINA, debeMostrar,
  );

  const deleteCredential = useDeleteCredential();
  const deshabilitar = useDeshabilitarCredencial();
  const habilitar = useHabilitarCredencial();
  const [apagarTarget, setApagarTarget] = useState<Credential | null>(null);
  const [motivoApagado, setMotivoApagado] = useState("");
  const [apagarError, setApagarError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Credential | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Credential | null>(null);
  const [testTarget, setTestTarget] = useState<Credential | null>(null);
  const [altaTarget, setAltaTarget] = useState<Credential | null>(null);
  const [altaAbierta, setAltaAbierta] = useState(false);
  const [importarAbierto, setImportarAbierto] = useState(false);
  const [loteAbierto, setLoteAbierto] = useState(false);
  const probarLote = useProbarEnLote(filtros);
  const [resultadoLote, setResultadoLote] = useState<ProbarEnLoteResult | null>(null);
  const [deleteError, setDeleteError] = useState("");

  function toggle(k: FacetKey, v: string) {
    setPagina(1);
    setSel((s) => ({
      ...s,
      [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v],
    }));
  }
  function clearFacet(k: FacetKey) {
    setPagina(1);
    setSel((s) => ({ ...s, [k]: [] }));
  }
  function limpiarTodo() {
    setPagina(1);
    setSearch("");
    setMostrarTodo(false);
    setSel({ client: [], device_type: [], protocol: [], test_status: [], site: [] });
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleteError("");
    try {
      await deleteCredential.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(
        err instanceof ApiError
          ? ((err.body as { error?: { message?: string } })?.error?.message ?? err.message)
          : "Error al eliminar",
      );
    }
  }

  const credentials = data?.items ?? [];
  const total = data?.total ?? 0;
  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA));
  const totalInventario = facetsQuery.data?.total;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <KeyRound className="size-6" />
            Credenciales de equipos
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Las credenciales que AROC usa para entrar a los equipos. Se prueban aquí
            y el SOAR las consume de aquí.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportarAbierto(true)}>
            <Download className="size-4 mr-2" />
            Importar de Zabbix
          </Button>
          <Button
            variant="outline"
            onClick={() => { setAltaTarget(null); setAltaAbierta(true); }}
          >
            <Radio className="size-4 mr-2" />
            Dar de alta un equipo
          </Button>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4 mr-2" />
            Nueva credencial
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[200px_minmax(0,1fr)] gap-6 items-start">
        <aside className="md:sticky md:top-4">
          <FacetRail
            facets={facetsQuery.data}
            filtros={filtros}
            onToggle={toggle}
            onClear={clearFacet}
          />
        </aside>

        <div className="space-y-3">
          <div className="flex gap-2 items-center flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPagina(1); }}
                placeholder="Buscar por nombre, usuario, cliente o sitio..."
                className="pl-9"
              />
            </div>
            <Button variant="outline" onClick={limpiarTodo}>Limpiar</Button>
            {hayFiltro && total > 0 && (
              <>
                <Button variant="outline" onClick={() => setLoteAbierto(true)}>
                  <KeyIcon className="size-4 mr-2" />
                  Poner credencial a los {total}
                </Button>
                <Button
                  variant="outline"
                  disabled={probarLote.isPending}
                  onClick={async () => {
                    setResultadoLote(null);
                    try {
                      setResultadoLote(await probarLote.mutateAsync());
                    } catch {
                      /* el detalle sale en la fila de cada credencial */
                    }
                  }}
                >
                  <Wifi className="size-4 mr-2" />
                  {probarLote.isPending ? `Probando ${total}…` : `Probar los ${total}`}
                </Button>
              </>
            )}
            <span className="ml-auto text-sm text-muted-foreground">
              {debeMostrar ? (
                <><span className="font-semibold text-foreground tabular-nums">{total}</span>
                  {typeof totalInventario === "number" ? ` de ${totalInventario}` : ""} credenciales</>
              ) : (
                <><span className="font-semibold text-foreground tabular-nums">{totalInventario ?? "—"}</span> credenciales en total</>
              )}
            </span>
          </div>

          {resultadoLote && (
            <div className="rounded-md border p-3 text-sm space-y-1">
              <p>
                Se probaron <b className="tabular-nums">{resultadoLote.probadas}</b>:{" "}
                <span className="text-green-600 dark:text-green-400">{resultadoLote.ok} correctas</span>
                {" · "}
                <span className="text-red-600 dark:text-red-400">{resultadoLote.rechazadas} rechazadas</span>
                {" · "}
                <span className="text-amber-600 dark:text-amber-400">
                  {resultadoLote.no_concluyentes} no concluyentes
                </span>
              </p>
              {resultadoLote.omitidas.length > 0 && (
                <details className="text-xs text-muted-foreground">
                  <summary className="cursor-pointer">
                    {resultadoLote.omitidas.length} no se pudieron probar — ver por qué
                  </summary>
                  <ul className="mt-1 space-y-0.5 pl-4">
                    {resultadoLote.omitidas.slice(0, 40).map((o) => (
                      <li key={o.nombre}>
                        <span className="font-mono">{o.nombre}</span>: {o.motivo}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              <button
                type="button"
                className="text-xs text-primary hover:underline"
                onClick={() => setResultadoLote(null)}
              >
                Ocultar
              </button>
            </div>
          )}

          {/* Filtros activos, para que se vea qué se está aplicando sin
              tener que recorrer el riel con la vista. */}
          {hayFiltro && (
            <div className="flex flex-wrap gap-1.5">
              {(Object.keys(sel) as FacetKey[]).flatMap((k) =>
                sel[k].map((v) => (
                  <button
                    key={`${k}:${v}`}
                    type="button"
                    onClick={() => toggle(k, v)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-accent px-2.5 py-0.5 text-xs"
                  >
                    <span className="text-muted-foreground">{FACET_TITULOS[k]}</span>
                    {etiquetaFaceta(k, v)}
                    <X className="size-3" />
                  </button>
                )),
              )}
              {search.trim() && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="inline-flex items-center gap-1.5 rounded-full bg-accent px-2.5 py-0.5 text-xs"
                >
                  <span className="text-muted-foreground">Texto</span>
                  {search.trim()}
                  <X className="size-3" />
                </button>
              )}
            </div>
          )}

          {!debeMostrar ? (
            <div className="border rounded-lg p-10 text-center space-y-3">
              <KeyRound className="size-10 mx-auto opacity-30" />
              <p className="font-medium">Elige un filtro para empezar</p>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Mostrar todas de entrada no ayuda a encontrar ninguna. Marca un cliente
                o un tipo de equipo a la izquierda, o busca por nombre.
              </p>
              <Button variant="outline" onClick={() => setMostrarTodo(true)}>
                Mostrar todas{typeof totalInventario === "number" ? ` (${totalInventario})` : ""}
              </Button>
              <p className="text-xs text-muted-foreground pt-1">
                Los números junto a cada filtro dicen cuántas quedarían.
              </p>
            </div>
          ) : isLoading ? (
            <p className="text-sm text-muted-foreground">Cargando...</p>
          ) : credentials.length === 0 ? (
            <div className="border rounded-lg p-8 text-center text-muted-foreground space-y-2">
              <KeyRound className="size-10 mx-auto opacity-30" />
              <p>Ninguna credencial cumple esos filtros.</p>
              <p className="text-xs">Quita alguno para ampliar la búsqueda.</p>
            </div>
          ) : (
            <>
              <div className="border rounded-lg divide-y">
                {credentials.map((cred) => (
                  <div key={cred.id} className="flex items-center justify-between px-4 py-3">
                    <div className="space-y-0.5 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-medium">{cred.name}</span>
                        {cred.description && (
                          <span className="text-xs text-muted-foreground truncate">— {cred.description}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                        {[cred.client, cred.site, cred.device_type]
                          .filter((v): v is string => !!v)
                          .map((v) => (
                            <span key={v} className="rounded border px-1.5 py-px text-[11px] text-muted-foreground">
                              {v}
                            </span>
                          ))}
                        <span className="rounded border px-1.5 py-px text-[11px] text-muted-foreground">
                          {PROTOCOL_LABELS[cred.protocol] ?? cred.protocol}
                        </span>
                        {cred.disabled_at && (
                          <span
                            className="rounded border border-amber-500/50 bg-amber-500/10 px-1.5 py-px text-[11px] text-amber-700 dark:text-amber-400"
                            title={`Deshabilitado por ${cred.disabled_by ?? "—"}: ${cred.disabled_reason ?? ""}`}
                          >
                            deshabilitado
                          </span>
                        )}
                        {cred.en_uso && (
                          <span
                            className="rounded border border-primary/40 bg-primary/5 px-1.5 py-px text-[11px]"
                            title={`En uso por ${cred.en_uso_detalle ?? ""}`}
                          >
                            en uso
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Usuario: <span className="font-mono">{cred.username}</span>
                        {cred.test_address ? (
                          // Por dónde entra AROC. Se muestra explícito porque para
                          // estos equipos NO es la IP de Zabbix, y sin verla parece
                          // que "no jala ninguna IP".
                          <>
                            {" · "}
                            <span className="font-mono text-sky-600 dark:text-sky-400" title="Dirección de prueba">
                              → {cred.test_address}{cred.port ? `:${cred.port}` : ""}
                            </span>
                          </>
                        ) : null}
                        {cred.zabbix_host_name ? (
                          <>
                            {" · "}
                            <span className="text-green-600 dark:text-green-400 font-mono">
                              ⬡ {cred.zabbix_host_name}
                            </span>
                          </>
                        ) : (
                          <>
                            {" · "}
                            <span className="text-amber-600 dark:text-amber-400">Sin host Zabbix</span>
                          </>
                        )}
                        {" · "}
                        {cred.last_tested_at === null ? (
                          <span className="text-muted-foreground">Sin probar</span>
                        ) : cred.last_tested_status === "no_concluyente" ? (
                          // Ámbar, no rojo: no dice nada sobre la credencial, así
                          // que no debe leerse como "está mal, bórrala".
                          <span
                            className="text-amber-600 dark:text-amber-400"
                            title={cred.last_tested_detail ?? undefined}
                          >
                            ? No concluyente {new Date(cred.last_tested_at).toLocaleDateString("es-MX")}
                          </span>
                        ) : cred.last_tested_ok ? (
                          <span className="text-green-600 dark:text-green-400">
                            ✓ OK {new Date(cred.last_tested_at).toLocaleDateString("es-MX")}
                            {cred.tested_by ? ` · ${cred.tested_by}` : ""}
                          </span>
                        ) : (
                          <span
                            className="text-red-600 dark:text-red-400"
                            title={cred.last_tested_detail ?? undefined}
                          >
                            ✗ Rechazada {new Date(cred.last_tested_at).toLocaleDateString("es-MX")}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 ml-4">
                      {!cred.zabbix_host_name && (
                        <Button
                          size="sm" variant="ghost"
                          title="Dar de alta en el monitoreo"
                          onClick={() => { setAltaTarget(cred); setAltaAbierta(true); }}
                        >
                          <Radio className="size-4" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" title="Probar conexión" onClick={() => setTestTarget(cred)}>
                        <Wifi className="size-4" />
                      </Button>
                      <Button size="sm" variant="ghost" title="Editar" onClick={() => setEditTarget(cred)}>
                        <Pencil className="size-4" />
                      </Button>
                      {cred.disabled_at ? (
                        <Button
                          size="sm" variant="ghost" title="Volver a habilitar"
                          onClick={() => void habilitar.mutateAsync(cred.id)}
                        >
                          <Power className="size-4" />
                        </Button>
                      ) : (
                        <Button
                          size="sm" variant="ghost"
                          title="Deshabilitar — el equipo deja de poder autenticarse, pero queda el registro"
                          className="text-amber-600 hover:text-amber-600"
                          onClick={() => { setApagarTarget(cred); setMotivoApagado(""); setApagarError(""); }}
                        >
                          <PowerOff className="size-4" />
                        </Button>
                      )}
                      {/* Borrar solo se ofrece si nadie la usa. En uso se
                          deshabilita: así queda registro de qué se apagó. */}
                      {!cred.en_uso && (
                        <Button
                          size="sm" variant="ghost" title="Eliminar"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setDeleteTarget(cred)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {paginas > 1 && (
                <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground">
                  <Button
                    variant="outline" size="sm"
                    disabled={pagina === 1}
                    onClick={() => setPagina((p) => p - 1)}
                  >
                    Anterior
                  </Button>
                  <span>
                    Página <span className="tabular-nums font-medium text-foreground">{pagina}</span>
                    {" de "}<span className="tabular-nums">{paginas}</span>
                  </span>
                  <Button
                    variant="outline" size="sm"
                    disabled={pagina >= paginas}
                    onClick={() => setPagina((p) => p + 1)}
                  >
                    Siguiente
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {createOpen && (
        <ModalShell title="Nueva credencial" onClose={() => setCreateOpen(false)}>
          <CredentialForm onClose={() => setCreateOpen(false)} />
        </ModalShell>
      )}
      {editTarget && (
        <ModalShell title="Editar credencial" onClose={() => setEditTarget(null)}>
          <CredentialForm initial={editTarget} onClose={() => setEditTarget(null)} />
        </ModalShell>
      )}
      {loteAbierto && (
        <ModalShell title="Poner la misma credencial a varios" onClose={() => setLoteAbierto(false)}>
          <AsignarEnLotePanel
            filtros={filtros}
            cuantos={total}
            descripcionFiltro={
              [
                ...(Object.keys(sel) as FacetKey[]).flatMap((k) =>
                  sel[k].map((v) => etiquetaFaceta(k, v)),
                ),
                ...(search.trim() ? [`"${search.trim()}"`] : []),
              ].join(" · ") || "sin filtro"
            }
            onClose={() => setLoteAbierto(false)}
          />
        </ModalShell>
      )}
      {importarAbierto && (
        <ModalShell title="Importar equipos de Zabbix" onClose={() => setImportarAbierto(false)}>
          <ImportarZabbixPanel onClose={() => setImportarAbierto(false)} />
        </ModalShell>
      )}
      {altaAbierta && (
        <ModalShell
          title="Dar de alta un equipo"
          onClose={() => { setAltaAbierta(false); setAltaTarget(null); }}
        >
          <AltaEquipoPanel
            cred={altaTarget}
            onClose={() => { setAltaAbierta(false); setAltaTarget(null); }}
          />
        </ModalShell>
      )}
      {testTarget && (
        <ModalShell title="Probar conexión" onClose={() => setTestTarget(null)}>
          <TestPanel cred={testTarget} onClose={() => setTestTarget(null)} />
        </ModalShell>
      )}
      {apagarTarget && (
        <ModalShell title="Deshabilitar equipo" onClose={() => setApagarTarget(null)}>
          <form
            className="space-y-4"
            onSubmit={async (e) => {
              e.preventDefault();
              setApagarError("");
              try {
                await deshabilitar.mutateAsync({ id: apagarTarget.id, motivo: motivoApagado });
                setApagarTarget(null);
              } catch (err) {
                setApagarError(
                  err instanceof ApiError
                    ? ((err.body as { error?: { message?: string } })?.error?.message ?? err.message)
                    : "No se pudo deshabilitar",
                );
              }
            }}
          >
            <p className="text-sm">
              <span className="font-mono font-medium">{apagarTarget.name}</span> deja de poder
              autenticarse: el SOAR, el control de configuración y ECC lo rechazan.
            </p>
            <p className="text-xs text-muted-foreground">
              Sigue en el inventario con toda su historia. Se puede volver a habilitar.
            </p>
            <div className="space-y-1">
              <Label>Motivo</Label>
              <Input
                value={motivoApagado}
                onChange={(e) => setMotivoApagado(e.target.value)}
                placeholder="ej: equipo retirado del sitio"
                required
              />
              <p className="text-xs text-muted-foreground">
                Queda registrado con tu usuario y la fecha.
              </p>
            </div>
            {apagarError && <p className="text-sm text-destructive">{apagarError}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setApagarTarget(null)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={!motivoApagado.trim() || deshabilitar.isPending}>
                {deshabilitar.isPending ? "Deshabilitando…" : "Deshabilitar"}
              </Button>
            </div>
          </form>
        </ModalShell>
      )}
      {deleteTarget && (
        <ModalShell title="Eliminar credencial" onClose={() => setDeleteTarget(null)}>
          <div className="space-y-4">
            <p className="text-sm">
              ¿Eliminar <span className="font-mono font-medium">{deleteTarget.name}</span>?
            </p>
            {deleteTarget.en_uso ? (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
                <p className="font-medium">Está en uso por {deleteTarget.en_uso_detalle}.</p>
                <p className="mt-1 text-xs">
                  No se puede borrar mientras esté en uso. Quita primero el uso,
                  o deshabilítala: el equipo deja de poder autenticarse y queda
                  el registro de quién lo apagó.
                </p>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                No la referencia ningún perfil, ni el SOAR, ni ECC.
              </p>
            )}
            {deleteError && <p className="text-sm text-destructive">{deleteError}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
              <Button
                variant="destructive"
                onClick={() => confirmDelete()}
                disabled={deleteCredential.isPending || deleteTarget.en_uso}
              >
                Eliminar
              </Button>
            </div>
          </div>
        </ModalShell>
      )}
    </div>
  );
}
